import type { TimeDraft } from './flag-drafts';
import type { FlagManager, FlagSchedule, ScheduleBody } from './useFlag';
import { useState } from 'react';
import { toast } from '@/components/ui/sonner';
import { parseTimeDraft, toLocalInput } from './flag-drafts';

type ScheduleEditorCopy = { saved: string; incomplete: string };

type UseScheduleEditorParams = { manager: FlagManager; schedule: FlagSchedule; copy: ScheduleEditorCopy };

const inputOf = (ms: number | null) => (ms ? toLocalInput(ms) : '');

/** The text a field shows: the draft while there is one, the saved time otherwise. */
const fieldText = (draft: TimeDraft, saved: number | null) =>
  draft === null ? inputOf(saved) : draft === 'clear' ? '' : draft.text;

/**
 * The Schedule card: a publish time (drafts only) and a turn-off time, saved together. A field
 * is cleared only by its Clear button; a half typed date blocks Save instead of clearing it.
 * Once the flag is no longer a draft its publish time is out of the draft, since the API
 * refuses one on a live flag.
 */
export const useScheduleEditor = ({ manager, schedule, copy }: UseScheduleEditorParams) => {
  const [publishDraft, setPublishDraft] = useState<TimeDraft>(null);
  const [offDraft, setOffDraft] = useState<TimeDraft>(null);

  // A publish time typed on a draft that has since been published (by hand or by the scheduler)
  // has nothing left to set.
  const publishAt = manager.isDraft ? publishDraft : null;
  const parsedPublish = parseTimeDraft(publishAt);
  const parsedOff = parseTimeDraft(offDraft);

  const isDirty = publishAt !== null || offDraft !== null;
  const isLocked = manager.isArchived || !manager.canChange;
  const blockingReason = !parsedPublish.ok || !parsedOff.ok ? copy.incomplete : undefined;

  const discard = () => {
    setPublishDraft(null);
    setOffDraft(null);
  };

  const save = async () => {
    if (!isDirty || !parsedPublish.ok || !parsedOff.ok) return;
    const body: ScheduleBody = {
      ...(parsedPublish.value !== undefined ? { publishAt: parsedPublish.value } : {}),
      ...(parsedOff.value !== undefined ? { offAt: parsedOff.value } : {}),
    };
    if (await manager.saveSchedule(body)) {
      discard();
      toast.success(copy.saved);
    }
  };

  return {
    showsPublishAt: manager.isDraft,
    publishText: fieldText(publishAt, schedule.publishAt),
    offText: fieldText(offDraft, schedule.offAt),
    setPublishText: (text: string) => setPublishDraft({ text }),
    setOffText: (text: string) => setOffDraft({ text }),
    clearPublish: () => setPublishDraft('clear'),
    clearOff: () => setOffDraft('clear'),
    // Clear is offered while a field shows a date or holds a half typed one.
    canClearPublish: !isLocked && (publishAt === null ? schedule.publishAt !== null : publishAt !== 'clear'),
    canClearOff: !isLocked && (offDraft === null ? schedule.offAt !== null : offDraft !== 'clear'),
    isLocked,
    isDirty,
    blockingReason,
    isSaving: manager.pending === 'schedule',
    save,
    discard,
  };
};

export type ScheduleEditorManager = ReturnType<typeof useScheduleEditor>;
